import { FREEZE_0_2, listDashPhoneVersions } from "../../case-spec";
import { useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import {
  api,
  ApiError,
  type BulkPromotePhoneRuntimeResult,
} from "@/lib/api";
import { cn } from "@/lib/utils";

const knownVersions = listDashPhoneVersions();
const defaultTarget = knownVersions.includes(FREEZE_0_2)
  ? FREEZE_0_2
  : (knownVersions[knownVersions.length - 1] ?? "0.1.3");

const pinSelectClassName = cn(
  "h-7 w-full max-w-xs rounded-md border border-input bg-input/20 px-2 py-0.5 text-sm outline-none",
  "focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30",
  "disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30",
);

function actionBadgeVariant(
  action: BulkPromotePhoneRuntimeResult["items"][number]["action"],
): "success" | "warning" | "destructive" | "outline" | "secondary" {
  switch (action) {
    case "would_update":
      return "warning";
    case "updated":
      return "success";
    case "failed":
      return "destructive";
    case "skip":
      return "secondary";
    default:
      return "outline";
  }
}

export function PhoneRuntimeBulkPromote() {
  const [toVersion, setToVersion] = useState(defaultTarget);
  const [preview, setPreview] = useState<BulkPromotePhoneRuntimeResult | null>(
    null,
  );
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fromVersions = useMemo(
    () => knownVersions.filter((v) => v !== toVersion),
    [toVersion],
  );

  const dryRunMutation = useMutation({
    mutationFn: () =>
      api.bulkPromotePhoneRuntime({
        toVersion,
        fromVersions,
        dryRun: true,
      }),
    onSuccess: (result) => {
      setPreview(result);
      toast.add({
        type: "success",
        title: "试运行完成",
        description: `${result.updated} 个包将迁移到 ${result.toVersion}`,
      });
    },
    onError: (err) => {
      setPreview(null);
      const code = err instanceof ApiError ? err.code : "request_failed";
      toast.add({
        type: "error",
        title: "试运行失败",
        description: code,
      });
    },
  });

  const applyMutation = useMutation({
    mutationFn: () =>
      api.bulkPromotePhoneRuntime({
        toVersion,
        fromVersions,
        dryRun: false,
      }),
    onSuccess: (result) => {
      setPreview(result);
      setConfirmOpen(false);
      toast.add({
        type: result.ok ? "success" : "error",
        title: result.ok
          ? "批量升级已应用"
          : "批量升级完成，但有错误",
        description: `updated=${result.updated} failed=${result.failed} opapi=${result.opapiCases}`,
      });
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : "request_failed";
      toast.add({
        type: "error",
        title: "批量升级失败",
        description: code,
      });
    },
  });

  const busy = dryRunMutation.isPending || applyMutation.isPending;
  const canApply =
    preview != null &&
    preview.dryRun &&
    preview.toVersion === toVersion &&
    preview.updated > 0 &&
    preview.failed === 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>批量升级手机运行时</CardTitle>
        <CardDescription>
          将兼容 pin（相同 validatorId）上的已发布包迁移到目标具体版本。会更新 D1
          pin、已发布 JSON，并重新导入 OPAPI。
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Alert>
          <AlertTitle>残留壳风险</AlertTitle>
          <AlertDescription>
            相同 validatorId 表示结构内容兼容。手机壳 UI 与加载行为在同一校验器类别内仍可能变化。这不是零风险迁移。
          </AlertDescription>
        </Alert>

        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="bulk-promote-target">目标版本</FieldLabel>
            <select
              id="bulk-promote-target"
              className={pinSelectClassName}
              value={toVersion}
              disabled={busy}
              onChange={(e) => {
                setToVersion(e.target.value);
                setPreview(null);
              }}
            >
              {knownVersions.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
            <FieldDescription>
              源版本为其余 {fromVersions.length}{" "}
              个相同 validator 的已知 pin，不含目标版本。
            </FieldDescription>
          </Field>
        </FieldGroup>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={busy || fromVersions.length === 0}
            onClick={() => dryRunMutation.mutate()}
          >
            {dryRunMutation.isPending ? <Spinner /> : null}
            试运行
          </Button>

          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogTrigger
              render={
                <Button type="button" disabled={busy || !canApply} />
              }
            >
              {applyMutation.isPending ? <Spinner /> : null}
              应用升级
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  将 {preview?.updated ?? 0} 个包升级到 {toVersion}？
                </AlertDialogTitle>
                <AlertDialogDescription>
                  将重写已发布的 CDN JSON，把 D1 pin 设为 {toVersion}，并重新导入每个案例到
                  OPAPI。相同 validatorId 不能保证壳行为完全一致。
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>取消</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => applyMutation.mutate()}
                  disabled={applyMutation.isPending}
                >
                  确认升级
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {preview ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="secondary">
                {preview.dryRun ? "试运行" : "已应用"}
              </Badge>
              <span className="text-muted-foreground">
                到 {preview.toVersion} · validator {preview.validatorId} ·
                匹配 {preview.matched} · 更新 {preview.updated} · 跳过{" "}
                {preview.skipped} · 失败 {preview.failed}
              </span>
            </div>

            {preview.sampleCaseIds.length > 0 ? (
              <p className="text-xs text-muted-foreground">
                样例案例：{preview.sampleCaseIds.join(", ")}
              </p>
            ) : null}

            {preview.items.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>案例</TableHead>
                      <TableHead>语言</TableHead>
                      <TableHead>来源</TableHead>
                      <TableHead>目标</TableHead>
                      <TableHead>操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.items.slice(0, 50).map((item) => (
                      <TableRow
                        key={`${item.versionId}:${item.locale}:${item.action}`}
                      >
                        <TableCell className="font-medium">
                          {item.caseId}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {item.locale}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {item.fromVersion}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {item.toVersion}
                        </TableCell>
                        <TableCell>
                          <Badge variant={actionBadgeVariant(item.action)}>
                            {item.action}
                            {item.reason ? ` · ${item.reason}` : ""}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                没有与源 pin 匹配的已发布包。
              </p>
            )}

            {preview.items.length > 50 ? (
              <p className="text-xs text-muted-foreground">
                显示前 50 条，共 {preview.items.length} 条。
              </p>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
